import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../../services/api';
import { Project } from '../../types';
import './TrashDialog.css';

interface TrashDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectRestored?: () => void;
}

const TrashDialog: React.FC<TrashDialogProps> = ({ isOpen, onClose, onProjectRestored }) => {
  const [deletedProjects, setDeletedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadDeletedProjects();
    }
  }, [isOpen]);

  const loadDeletedProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await projectsAPI.getDeleted();
      setDeletedProjects(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Chyba pri načítaní vymazaných projektov');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (projectId: number) => {
    try {
      await projectsAPI.restore(projectId);
      await loadDeletedProjects();
      if (onProjectRestored) {
        onProjectRestored();
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Chyba pri obnovení projektu');
    }
  };

  const handlePermanentDelete = async (projectId: number, projectName: string) => {
    if (!window.confirm(`Naozaj chcete natrvalo vymazať projekt "${projectName}"?\n\nTáto akcia sa nedá vrátiť späť!`)) {
      return;
    }

    try {
      await projectsAPI.permanentDelete(projectId);
      await loadDeletedProjects();
      if (onProjectRestored) {
        onProjectRestored();
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Chyba pri vymazaní projektu');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('sk-SK');
  };

  const getDaysUntilPermanentDelete = (deletedAt: string | null) => {
    if (!deletedAt) return null;
    const deleted = new Date(deletedAt);
    const now = new Date();
    const diffMs = now.getTime() - deleted.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const daysLeft = 60 - diffDays;
    return daysLeft > 0 ? daysLeft : 0;
  };

  if (!isOpen) return null;

  return (
    <div className="trash-dialog-overlay" onClick={onClose}>
      <div className="trash-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="trash-dialog-header">
          <h2>🗑️ Kôš</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="trash-dialog-content">
          {loading && <div className="loading">Načítavam...</div>}
          
          {error && <div className="error-message">{error}</div>}

          {!loading && !error && deletedProjects.length === 0 && (
            <div className="empty-trash">
              <p>Kôš je prázdny</p>
            </div>
          )}

          {!loading && !error && deletedProjects.length > 0 && (
            <div className="deleted-projects-list">
              <p className="trash-info">
                Vymazané projekty sa automaticky natrvalo vymažú po 60 dňoch.
              </p>
              
              {deletedProjects.map((project) => {
                const daysLeft = getDaysUntilPermanentDelete(project.deleted_at || null);
                return (
                  <div key={project.id} className="deleted-project-item">
                    <div className="project-info">
                      <h3>{project.name}</h3>
                      {project.description && <p className="project-description">{project.description}</p>}
                      <div className="project-meta">
                        <span>Vymazané: {formatDate(project.deleted_at || null)}</span>
                        {daysLeft !== null && (
                          <span className={`days-left ${daysLeft <= 7 ? 'warning' : ''}`}>
                            {daysLeft > 0 ? `Zostáva ${daysLeft} dní` : 'Vymaže sa dnes'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="project-actions">
                      <button
                        className="btn-restore"
                        onClick={() => handleRestore(project.id)}
                        title="Obnoviť projekt"
                      >
                        ↩️ Obnoviť
                      </button>
                      <button
                        className="btn-permanent-delete"
                        onClick={() => handlePermanentDelete(project.id, project.name)}
                        title="Natrvalo vymazať"
                      >
                        ❌ Natrvalo vymazať
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="trash-dialog-footer">
          <button className="btn-close" onClick={onClose}>Zavrieť</button>
        </div>
      </div>
    </div>
  );
};

export default TrashDialog;

